import raw from "./equipment-products.json";

/** Inferred from the recovered WooCommerce term members (see pack INVENTORY.md). */
export const EQUIPMENT_CATEGORIES = [
  { id: "manufactured-equipment", label: "Manufactured equipment" },
  { id: "services", label: "Services" },
  { id: "test-equipment", label: "Test equipment" },
  { id: "lcd-panel-services", label: "LCD panel services" },
  { id: "touch-screen-panels", label: "Touch screen panels" },
  { id: "connectors", label: "Connectors / interfaces" },
  { id: "specialty-cables", label: "Specialty cables" },
  { id: "consumer-cables", label: "Consumer cables" },
  { id: "antennas-iot", label: "Antennas / IoT" },
  { id: "modules", label: "Modules" },
  { id: "power-supplies", label: "Power supplies" },
  { id: "digital-displays", label: "Digital displays / FIDS" },
  { id: "pos-systems", label: "POS systems" },
  { id: "professional-drone-systems", label: "Professional drone systems" },
  { id: "safety-ppe", label: "Safety PPE / workwear" },
] as const;

export type EquipmentCategoryId = (typeof EQUIPMENT_CATEGORIES)[number]["id"];

export type EquipmentProduct = {
  id: string;
  slug: string;
  title: string;
  date: string;
  status: string;
  categoryId: EquipmentCategoryId;
};

const CATEGORY_BY_SLUG: Record<string, EquipmentCategoryId> = {
  "temperature-controlled-chucks": "manufactured-equipment",
  "hast-systems-highly-accelerated-stress-tester": "manufactured-equipment",
  centrifuges: "manufactured-equipment",
  "bubble-tester-and-fine-gross-leak-system": "manufactured-equipment",
  "temperature-humidity-and-pressure-systems": "manufactured-equipment",
  "customized-test-equipment": "manufactured-equipment",
  "feeder-bowl-system": "manufactured-equipment",

  "installation-commissioning": "services",
  "calibration-services": "services",
  "preventive-maintenance": "services",
  "repair-services": "services",
  "equipment-relocation": "services",
  "spare-parts-oem-parts-inventory-support": "services",
  "upgrading-refurbished-equipment": "services",

  "environmental-test-chambers": "test-equipment",
  "vibration-shock-test-equipment": "test-equipment",
  "drop-test-system": "test-equipment",
  "burn-in-test-system-test-boards": "test-equipment",

  "lamination-service-lcd-panels": "lcd-panel-services",
  "brightness-enhancement-lcd-panels-modules": "lcd-panel-services",

  "touch-screen-panels": "touch-screen-panels",

  ddr: "connectors",
  usb: "connectors",
  powerusb: "connectors",
  pcie: "connectors",
  sata: "connectors",
  esata: "connectors",
  "d-sub": "connectors",
  rj45: "connectors",

  "micro-coaxil-cable": "specialty-cables",
  "auto-mobile-cable": "specialty-cables",
  "medical-cable": "specialty-cables",

  "usb-3-1-cable": "consumer-cables",
  "hdmi-cable": "consumer-cables",
  "audio-cable": "consumer-cables",
  "lightning-mfi-cable": "consumer-cables",
  "waterproof-cable": "consumer-cables",

  "internal-antenna-pcb-fpc-pifa-gps": "antennas-iot",
  "external-antenna-indoor-outdoor": "antennas-iot",
  "patch-antenna": "antennas-iot",
  "iot-cable": "antennas-iot",

  "fingerprint-module": "modules",
  "camera-module": "modules",
  "lcd-liquid-crystal-module": "modules",
  "led-backlight-module": "modules",
  "control-panel": "modules",
  "vr-smart-glasses-module": "modules",
  "drone-module": "modules",
  "driving-system": "modules",

  "pc-power-supply": "power-supplies",
  "psu-power-supply": "power-supplies",
  "adapter-power-supply": "power-supplies",
  "open-frame-power-supply": "power-supplies",
  "ipc-psu-power-supply": "power-supplies",
  "medical-psu-power-supply": "power-supplies",
  "ssl-power-supply": "power-supplies",

  "digital-menu-board": "digital-displays",
  "digital-poster": "digital-displays",
  "vcs-presentation": "digital-displays",
  "pis-display-products": "digital-displays",
  "check-in-info-display": "digital-displays",
  "fids-flight-information-display-system": "digital-displays",

  "point-of-sale-pos-systems": "pos-systems",
  "professional-drone-system": "professional-drone-systems",

  "safety-shoes": "safety-ppe",
  "chef-shoes": "safety-ppe",
  "safety-gumboots": "safety-ppe",
  "safety-gloves": "safety-ppe",
  "safety-helmet": "safety-ppe",
  "safety-glasses": "safety-ppe",
  "safety-harness": "safety-ppe",
  "safety-cover-all": "safety-ppe",
  "fr-coverall": "safety-ppe",
  "work-suits": "safety-ppe",
  "work-pants": "safety-ppe",
  "reflective-shirt": "safety-ppe",
  "safety-jacket": "safety-ppe",
  raincoats: "safety-ppe",
  "lab-coats": "safety-ppe",
  "chef-uniform": "safety-ppe",
  "medical-crubs": "safety-ppe",
  "work-shirt": "safety-ppe",
  "reflecting-vest": "safety-ppe",
  "safety-earmuff": "safety-ppe",
  "face-masks": "safety-ppe",
};

export const EQUIPMENT_PRODUCTS: EquipmentProduct[] = raw.products.map((p) => ({
  id: p.id,
  slug: p.slug,
  title: p.title,
  date: p.date,
  status: p.status,
  categoryId: CATEGORY_BY_SLUG[p.slug] ?? "manufactured-equipment",
}));

const BY_SLUG = new Map(EQUIPMENT_PRODUCTS.map((p) => [p.slug, p]));

export function equipmentCategory(id: EquipmentCategoryId) {
  return EQUIPMENT_CATEGORIES.find((c) => c.id === id) ?? EQUIPMENT_CATEGORIES[0];
}

export function equipmentBySlug(slug: string): EquipmentProduct | undefined {
  return BY_SLUG.get(slug);
}

export function equipmentUncategorisedSlugs(): string[] {
  return raw.products.filter((p) => !CATEGORY_BY_SLUG[p.slug]).map((p) => p.slug);
}
